import { noop } from 'lodash';
import React from 'react';
import WindowedSVG from 'components/WindowedSVG';
import ObjectView from 'components/ObjectView';
import * as API from 'generated/api';
import Proto from 'generated/freeform_pb';
import { mat2d, vec2, Vec2 } from 'utility/gl-matrix';
import * as Rectangle from 'utility/Rectangle';

const dummyCreator = API.MinimalUserFromJSON({
  'username': 'david',
  'id': 'abcdef',
  'uuid': 'abcdef',
  'firstName': 'David',
  'displayName': 'David Lee',
  'lastName': 'Lee',
  'profileImageSmall': null,
  'profileImageLarge': null,
  'profileImageUuid': null,
});

// returns placement in content coordinate space
const getObjectFrame = (obj: Proto.FFObject): Rectangle.Rectangle => {
  const size = obj.getSize();
  const xf = obj.getTransform()?.toObject();
  if (size == null || xf == null) {
    return Rectangle.zero();
  }

  return Rectangle.aabbByTransforming(
    Rectangle.fromXYWidthHeight({
      ...size.toObject(),
      x: 0,
      y: 0,
    }),
    mat2d.fromComponents(xf)
  );
};

const getZIndex = (obj: Proto.FFObject) => (obj.getIsBackground() ? -1 : 1) * obj.getCreated().getSeconds();

interface Props {
  chat: API.FFPublicChat;
  viewportSize: Vec2;
  objects: Record<string, Proto.FFObject>;
  contentInset: { top: number, bottom: number };
  contentWidth: number;

  maxWidth: number;

  onViewportChanged: (minContentY: number, maxContentY: number) => void;

  children?: JSX.Element;
}

export function Chat({
  objects: allObjects,
  chat,
  maxWidth,
  contentWidth,
  onViewportChanged,
  viewportSize,
  contentInset,
}: Props) {
  // Sorted by effective z index - bottom of stack first
  const sortedObjects = React.useMemo(() => (
    Object.values(allObjects).sort((a, b) => getZIndex(a) - getZIndex(b))
  ), [allObjects]);

  return (
    <WindowedSVG
      topInset={contentInset.top}
      displayWidth={Math.min(maxWidth, vec2.x(viewportSize))}
      contentHeight={
        (chat.height ?? 0) + contentInset.bottom
      }
      contentWidth={contentWidth}
      initialContentOffsetY={0}
      onViewportChanged={onViewportChanged}
    >
      {(shouldShow) => (
        <>
          {
            sortedObjects
              .filter((obj) => shouldShow(getObjectFrame(obj)))
              .map((obj) => (
                <ObjectView
                  key={obj.getUuid()}
                  object={obj}
                  creator={dummyCreator}
                />
              ))
          }
        </>
      )}
    </WindowedSVG>
  );

}

Chat.defaultProps = {
  onObjectDragBegan: noop,
  onObjectDragMoved: noop,
  onObjectDragEnded: noop,
  onViewportChanged: noop,
};

export default Chat;
